import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api';

/**
 * „DZIAŁANIA NA BAZIE" — odczyt bazy wyspy jak w SSMS (etap 1, 28.09.2026).
 *
 * Kształty odpowiedzi: `api/src/baza/baza.service.ts`. Pola opcjonalne tylko tam, gdzie serwer
 * naprawdę może je pominąć — zod ostrzejszy niż serwis pokazałby udany odczyt jako błąd.
 */

export const WezelBazySchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  hostname: z.string().nullable(),
  automat: z.object({ nazwa: z.string(), vmid: z.number() }).nullable(),
  /** Automat, którego ta baza jest właścicielem — wzorzec dla tabel automatu przy wyrównaniu. */
  spsVmid: z.number().nullable(),
  master: z.boolean(),
  wersja: z.string().nullable(),
  obsluguje: z.boolean(),
  ostatniKontakt: z.string().nullable(),
});
export type WezelBazy = z.infer<typeof WezelBazySchema>;

const WezlyBazySchema = z.object({
  tryb: z.string(),
  wymaganaWersja: z.string(),
  wezly: z.array(WezelBazySchema),
});
export type WezlyBazy = z.infer<typeof WezlyBazySchema>;

export const fetchWezlyBazy = (groupId: string) => apiGet(`/baza/wezly?groupId=${groupId}`, WezlyBazySchema);

const ZlecenieSchema = z.object({ zadanieId: z.string() });

export const zlecZapytanie = (groupId: string, agentId: string, sql: string) =>
  apiPost('/baza/zapytanie', { groupId, agentId, sql }, ZlecenieSchema);

export const zlecSchemat = (groupId: string, agentId: string) =>
  apiPost('/baza/schemat', { groupId, agentId }, ZlecenieSchema);

export const WynikZapytaniaSchema = z.object({
  kolumny: z.array(z.object({ nazwa: z.string(), typ: z.string() })),
  wiersze: z.array(z.array(z.string().nullable())),
  obcieto: z.boolean(),
  ms: z.number(),
});
export type WynikZapytania = z.infer<typeof WynikZapytaniaSchema>;

export const TabelaSchematuSchema = z.object({
  nazwa: z.string(),
  widok: z.boolean(),
  zapis: z.enum(['BRAMKA', 'ZAKAZ', 'NIE']),
  automatu: z.boolean(),
  kolumny: z.array(
    z.object({ nazwa: z.string(), typ: z.string(), klucz: z.boolean(), pusta: z.boolean(), zapisywalna: z.boolean() }),
  ),
});
export type TabelaSchematu = z.infer<typeof TabelaSchematuSchema>;

const StanZapytaniaSchema = z.discriminatedUnion('stan', [
  z.object({ stan: z.literal('CZEKA'), nodeId: z.string() }),
  z.object({ stan: z.literal('BLAD'), nodeId: z.string(), blad: z.string() }),
  z.object({
    stan: z.literal('GOTOWE'),
    nodeId: z.string(),
    wynik: WynikZapytaniaSchema.optional(),
    schemat: z.array(TabelaSchematuSchema).optional(),
  }),
]);
export type StanZapytania = z.infer<typeof StanZapytaniaSchema>;

export const wynikZapytania = (groupId: string, zadanieId: string) =>
  apiGet(`/baza/wynik/${zadanieId}?groupId=${groupId}`, StanZapytaniaSchema);

/**
 * CZEKANIE NA WYNIK — odpytujemy co sekundę, jak podgląd obrazka.
 *
 * ⚠️ GRANICA CZASU PO NASZEJ STRONIE, a nie tylko po stronie agenta: zadanie dla węzła, który
 * zamilkł, czekałoby w kolejce bez końca, a ekran kręciłby kółkiem. Agent sam przerywa po 30 s,
 * więc 60 s znaczy „węzeł nie wziął zadania", i tak to mówimy.
 */
export async function czekajNaWynik(
  groupId: string,
  zadanieId: string,
  opcje: { limitMs?: number; coMs?: number; przerwano?: () => boolean } = {},
): Promise<StanZapytania> {
  const limit = opcje.limitMs ?? 60_000;
  const co = opcje.coMs ?? 1000;
  const start = Date.now();
  for (;;) {
    const s = await wynikZapytania(groupId, zadanieId);
    if (s.stan !== 'CZEKA') return s;
    if (opcje.przerwano?.() === true) return s;
    if (Date.now() - start > limit) {
      return { stan: 'BLAD', nodeId: s.nodeId, blad: `Węzeł ${s.nodeId} nie wziął zapytania przez ${Math.round(limit / 1000)} s` };
    }
    await new Promise((r) => setTimeout(r, co));
  }
}

/**
 * WYRÓWNANIE Z PORÓWNANIA — jedno źródło na żądanie (patrz `api/src/baza/baza.dto.ts`).
 * Wiersze to całe wiersze bazy źródła; przy „inne" dochodzą nazwy różniących się kolumn.
 */
export type WierszWyrownania = {
  /** `NADMIAR` (29.09.2026) — wiersza nie ma w bazie-wzorcu, więc znika z całej wyspy (tylko liście). */
  rodzaj: 'INNY' | 'BRAK' | 'NADMIAR';
  wartosci: Record<string, string | null>;
  kolumny?: string[];
};

export const wyrownaj = (groupId: string, tabela: string, zrodloAgentId: string, wiersze: WierszWyrownania[]) =>
  apiPost(
    '/baza/wyrownaj',
    { groupId, tabela, zrodloAgentId, wiersze },
    /** `usuniec` opcjonalne — starsza instancja go nie odda, a ostrzejszy schemat zrobiłby z sukcesu błąd. */
    z.object({ opId: z.string(), poprawek: z.number(), dolozen: z.number(), usuniec: z.number().optional(), pominiete: z.number() }),
  );

/**
 * STAN OPERACJI NA WĘZŁACH — pasek po wysyłce z siatki albo naprawie (29.09.2026).
 * Reguły stanu jednej bazy: `api/src/baza/stan-operacji.ts`.
 */
export const StanOperacjiSchema = z.object({
  opId: z.string(),
  /** Stan całej operacji — ten sam, co w zakładce „Zmiany". */
  status: z.string(),
  blad: z.string().nullable(),
  wezly: z.array(
    z.object({
      nodeId: z.string(),
      master: z.boolean(),
      stan: z.enum(['CZEKA', 'W_DRODZE', 'ZROBIONE', 'BLAD']),
      blad: z.string().nullable(),
      uwagi: z.string().nullable(),
      wykonanych: z.number(),
      pominietych: z.number(),
    }),
  ),
});
export type StanOperacji = z.infer<typeof StanOperacjiSchema>;

export const fetchStanOperacji = (groupId: string, opId: string) =>
  apiGet(`/baza/operacja/${opId}?groupId=${groupId}`, StanOperacjiSchema);

